import { DatePicker, type DatePickerProps } from '@heroui/react'
import { parseDate, parseDateTime } from '@internationalized/date'
import { I18nProvider } from '@react-aria/i18n'
import { useTranslation } from 'react-i18next'

/**
 * The locale the field formats its segments in. Always a day-first one: a
 * native `<input type="date">` takes its order from the viewer's browser or OS,
 * so a gym owner on a US-locale laptop saw month-first dates, and 03/04 reads
 * both ways. English falls back to en-GB for the same reason.
 */
export function dayFirstLocale(language: string | undefined): string {
  const base = (language ?? '').split('-')[0]
  if (base === 'ro') return 'ro-RO'
  if (base === 'fr') return 'fr-FR'
  return 'en-GB'
}

type DateFieldProps = Omit<
  DatePickerProps,
  'value' | 'defaultValue' | 'onChange' | 'minValue' | 'maxValue' | 'granularity'
> & {
  /** `YYYY-MM-DD`, or `YYYY-MM-DDTHH:mm` with `withTime`. Empty means no date. */
  value: string
  onValueChange: (value: string) => void
  /** Same format as `value`. */
  min?: string
  max?: string
  /** Pick a time as well, replacing `<input type="datetime-local">`. */
  withTime?: boolean
}

// HeroUI's props reach @internationalized/date through more than one copy, and
// its classes carry #private fields, so TypeScript treats identical classes as
// unrelated. Runtime is duck-typed, so type against the prop and cast once.
type PickerValue = NonNullable<DatePickerProps['value']>

function toDateValue(value: string | undefined, withTime: boolean): PickerValue | null {
  if (!value) return null
  try {
    const parsed = withTime ? parseDateTime(value.slice(0, 16)) : parseDate(value.slice(0, 10))
    return parsed as unknown as PickerValue
  } catch {
    return null
  }
}

/**
 * Drop-in replacement for `<Input type="date">` / `type="datetime-local"`. It
 * takes and emits the same strings the native input did, so nothing downstream
 * changes. Only the display is pinned to a day-first locale.
 */
export function DateField({
  value,
  onValueChange,
  min,
  max,
  withTime = false,
  ...props
}: DateFieldProps) {
  const { i18n } = useTranslation()
  const locale = dayFirstLocale(i18n.resolvedLanguage ?? i18n.language)

  return (
    <I18nProvider locale={locale}>
      <DatePicker
        showMonthAndYearPickers
        {...props}
        granularity={withTime ? 'minute' : 'day'}
        hourCycle={withTime ? 24 : undefined}
        hideTimeZone
        value={toDateValue(value, withTime)}
        minValue={toDateValue(min, withTime) as DatePickerProps['minValue']}
        maxValue={toDateValue(max, withTime) as DatePickerProps['maxValue']}
        onChange={(next) => {
          if (!next) return onValueChange('')
          const s = next.toString()
          onValueChange(withTime ? s.slice(0, 16) : s.slice(0, 10))
        }}
      />
    </I18nProvider>
  )
}
